import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, KeyRound, UserPlus } from 'lucide-react';
import {
  Department,
  UserRole,
  departmentLabelKey,
  departmentSchema,
  assignableByAdminSchema,
  suggestedDepartmentForRole,
  type AssignableByAdmin,
  type TempPasswordResponse,
} from '@nbh/shared';
import { adminApi, type AdminUser } from '../../lib/adminApi';

/**
 * Staff accounts (SPEC.md §2.2), on the root tier (decision D44).
 *
 * **One tier now.** This screen carried two: D2 of the credentials work let an ADMIN see
 * the roster and hand a forgotten password back, hiding only the lifecycle controls. The
 * isolation matrix took the whole surface to root, so the branching is gone rather than
 * inverted — a component that renders differently for a role that can never reach it is a
 * conditional nobody can check.
 *
 * The Staff tab is hidden from an ADMIN and every route behind this screen answers 403 to
 * one. The hiding is the courtesy; `users.admin.access.test.ts` is the control.
 */
export function UserManager() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credential, setCredential] = useState<TempPasswordResponse | null>(null);

  const load = useCallback(() => {
    adminApi.users
      .list()
      .then(setUsers)
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(load, [load]);

  async function act(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed');
    }
  }

  if (error && !users)
    return (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    );
  if (!users) return <p className="text-sm text-slate-400">{t('admin.loading')}</p>;

  return (
    <div className="space-y-4">
      <CreateUserForm
        onCreated={(created) => {
          setCredential(created);
          load();
        }}
        onError={setError}
      />

      {credential && (
        <TempPasswordNotice credential={credential} onDismiss={() => setCredential(null)} />
      )}

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
        {users.map((user) => (
          <li key={user.id} className="flex flex-wrap items-center gap-2 p-3">
            <span className="min-w-0 flex-1">
              <span
                className={`block truncate text-sm ${user.isActive ? 'text-slate-100' : 'text-slate-600 line-through'}`}
              >
                {user.name}
              </span>
              <span className="block truncate text-xs text-slate-500">
                {user.email} · {user.role} · {t(departmentLabelKey[user.department as Department])}
              </span>
            </span>

            {/*
              Offered against every account in the list, the seeded root one included. It
              used to be withheld there: an ADMIN could reach this screen, the reset
              returns the new password in its response, and reading a root account's
              credential is signing in as root. With the screen and the route both at root
              tier there is no rank left to cross — a super-admin resetting a super-admin
              is peer-level, and it is audited either way.
            */}
            <button
              type="button"
              onClick={() =>
                void act(async () => setCredential(await adminApi.users.resetPassword(user.id)))
              }
              className="inline-flex min-h-[40px] items-center gap-1 rounded bg-slate-800 px-3 text-xs text-slate-200"
            >
              <KeyRound className="size-3.5" aria-hidden="true" />
              {t('adminUsers.reset')}
            </button>

            <button
              type="button"
              onClick={() =>
                void act(() => adminApi.users.update(user.id, { isActive: !user.isActive }))
              }
              className="min-h-[40px] rounded bg-transparent px-3 text-xs text-slate-400"
            >
              {user.isActive ? t('adminUsers.deactivate') : t('adminUsers.reactivate')}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Shown once. The password is only ever stored hashed, so it cannot be retrieved
 * again — if it is lost before it reaches the person, reset it.
 */
function TempPasswordNotice({
  credential,
  onDismiss,
}: {
  credential: TempPasswordResponse;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border-2 border-amber-500/60 bg-amber-950/30 p-3">
      <p className="text-xs text-amber-200">
        {t('adminUsers.tempOnce', { email: credential.email })}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="rounded bg-slate-950 px-2 py-1 font-mono text-sm text-amber-100">
          {credential.temporaryPassword}
        </code>
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(credential.temporaryPassword)}
          className="inline-flex min-h-[40px] items-center gap-1 rounded bg-slate-800 px-3 text-xs text-slate-200"
        >
          <Copy className="size-3.5" aria-hidden="true" />
          {t('adminUsers.copy')}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="min-h-[40px] rounded bg-transparent px-3 text-xs text-slate-400"
        >
          {t('adminUsers.dismiss')}
        </button>
      </div>
    </div>
  );
}

function CreateUserForm({
  onCreated,
  onError,
}: {
  onCreated: (created: TempPasswordResponse) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // Narrower than UserRole on purpose, though no longer to stop an escalation — only root
  // reaches this form now, and the route behind it accepts the full range. The narrowing
  // is that appointing an admin or a second root account is a different act from adding a
  // technician, and it has its own form in the portal (`SuperAdminStaffForm`) where it
  // takes a written reason and reads as the governance decision it is.
  const [role, setRole] = useState<AssignableByAdmin>(UserRole.Maintenance);
  const [department, setDepartment] = useState<Department>(
    suggestedDepartmentForRole[UserRole.Maintenance] ?? Department.Administration,
  );

  /**
   * Pre-selects the obvious team so the common case stays one tap, while leaving the
   * field free: D22 exists precisely so an ADMIN can belong to MAINTENANCE. Roles
   * with no natural team leave whatever was already chosen alone.
   */
  function chooseRole(next: AssignableByAdmin) {
    setRole(next);
    const suggestion = suggestedDepartmentForRole[next];
    if (suggestion) setDepartment(suggestion);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!email.trim() || !name.trim()) return;

    try {
      // `POST /api/super-admin/users`, not the `admin/users` route this form used to
      // call. That one is gone (D44) — two routes minting accounts on one tier, and the
      // one wired to this form was the one that wrote no audit row, so an account could
      // appear on the property with nothing recording who made it.
      onCreated(
        await adminApi.superAdmin.users.create({
          email: email.trim(),
          name: name.trim(),
          role,
          department,
        }),
      );
      setEmail('');
      setName('');
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Failed');
    }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs text-slate-400">
        <UserPlus className="size-3.5" aria-hidden="true" />
        {t('adminUsers.addHeading')}
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('adminUsers.name')}
          aria-label={t('adminUsers.name')}
          className="min-w-[120px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t('adminUsers.email')}
          aria-label={t('adminUsers.email')}
          className="min-w-[140px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        />
        <select
          value={role}
          onChange={(event) => chooseRole(event.target.value as AssignableByAdmin)}
          aria-label={t('adminUsers.role')}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        >
          {assignableByAdminSchema.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <select
          value={department}
          onChange={(event) => setDepartment(event.target.value as Department)}
          aria-label={t('adminUsers.department')}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
        >
          {departmentSchema.options.map((option) => (
            <option key={option} value={option}>
              {t(departmentLabelKey[option])}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={!email.trim() || !name.trim()}
          className="min-h-[44px] rounded bg-brookfields-green ring-1 ring-brookfields-gold/70 px-4 text-sm font-medium text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
        >
          {t('adminUsers.add')}
        </button>
      </div>
    </form>
  );
}
